import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { useSiteContent } from "@/hooks/useSiteContent";

const COLUMNS = [
  {
    title: "PLATFORM",
    links: [
      { label: "Verification services", to: "/#services" },
      { label: "How it works", to: "/#process" },
      { label: "How it works for businesses", to: "/how-it-works/business" },
      { label: "How it works for individuals", to: "/how-it-works/individuals" },
      { label: "Coverage", to: "/#coverage" },
      { label: "Pricing", to: "/pricing" },
      { label: "Blog", to: "/blog" },
      { label: "Operator console", to: "/signin" },
      { label: "API documentation", to: "/developers" },
    ],
  },
  {
    title: "LEGAL",
    links: [
      { label: "Terms of Use", to: "/terms" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "California Privacy", to: "/california-privacy" },
      { label: "SMS Terms", to: "/messaging-terms" },
      { label: "Compliance framework", to: "/compliance" },
    ],
  },
  {
    title: "RIGHTS & SUPPORT",
    links: [
      { label: "Dispute a report", to: "/dispute" },
      { label: "Verify a report", to: "/verify" },
      { label: "Subject rights", to: "/subject-rights" },
      { label: "Contact us", to: "/contact" },
      { label: "Communication preferences", to: "/opt-in" },
      { label: "Business sign up", to: "/signup" },
      { label: "Free personal account", to: "/signup/personal" },
    ],
  },
];

const SOCIALS = [
  {
    id: "facebook",
    label: "ResidenceProof on Facebook",
    href: "https://www.facebook.com/profile.php?id=61595790187623",
    path: "M13.5 21.95v-7.7h2.6l.4-3h-3V9.33c0-.87.24-1.46 1.49-1.46h1.6V5.18a21.4 21.4 0 0 0-2.33-.12c-2.3 0-3.88 1.4-3.88 3.98v2.21H7.77v3h2.61v7.7A10 10 0 1 1 13.5 21.95Z",
  },
  {
    id: "linkedin",
    label: "ResidenceProof on LinkedIn",
    href: "https://www.linkedin.com/company/residenceproof/?viewAsMember=true",
    path: "M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13ZM7.12 20.45H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0Z",
  },
];

const SocialLinks = () => (
  <div className="mt-6 flex items-center gap-3" data-testid="footer-social-links">
    {SOCIALS.map((s) => (
      <a
        key={s.id}
        href={s.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={s.label}
        data-testid={`footer-social-${s.id}`}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-[color,background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-white hover:text-oath-navy"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true"><path d={s.path} /></svg>
      </a>
    ))}
  </div>
);

export const Footer = () => {
  const { contact, certifications } = useSiteContent();
  const certLine = certifications.length ? certifications.map((c) => c.title).join(" · ") : "CERTIFICATIONS PENDING REVIEW";
  const contactLine = [contact.support_email, contact.phone, contact.address].filter(Boolean).join(" · ");
  return (
    <footer className="w-full bg-oath-navy text-white" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div className="max-w-xs">
            <Logo light />
            <p className="mt-5 text-sm leading-relaxed text-white/60">
              Independent verification of residential address and household
              composition — evidence-first reporting for insurers and benefit
              programs.
            </p>
            <p className="mt-6 font-mono-label text-[9px] text-white/40" data-testid="footer-certifications">
              {certLine.toUpperCase()}
            </p>
            <SocialLinks />
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h4 className="font-mono-label text-[10px] font-semibold text-white/45">
                {col.title}
              </h4>
              <ul className="mt-4 space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.to}
                      className="text-sm text-white/75 transition-colors hover:text-white"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col gap-3 border-t border-white/10 pt-6 text-xs text-white/45 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} ResidenceProof. All rights reserved.</p>
          <p className="font-mono-label text-[9px]" data-testid="footer-contact">
            CONTACT · {contactLine}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
